import React, { Fragment } from "react";
import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Scrollbar from "../../../components/scrollbar/scrollbar";
import Footer from "../../../components/footer/Footer";
import img13 from "../../../images/service/brand/reputation management.png";
import ServiceHero from "../components/ServiceHero";
import ServiceJourney from "../components/ServiceJourney";
import ServiceCapabilities from "../components/ServiceCapabilities";
import ServiceResults from "../components/ServiceResults";
import ServiceOutcomes from "../components/ServiceOutcomes";
import ServiceWhyChooseUs from "../components/ServiceWhyChooseUs";
import FaqSection from "../../../components/PageSection/FAQsection";

const ReputationManagement = () => {
  const heroData = {
    title: "Online Reputation Management (ORM)",
    description: "Protect and enhance your brand's online reputation with proactive monitoring, review management, and strategic content. We build trust, suppress negative content, and amplify positive brand sentiment.",
    image: img13,
    imageAlt: "Reputation Management",
    features: [
      "Brand monitoring",
      "Review management",
      "Negative content suppression",
      "Crisis management",
      "Sentiment analysis",
      "Reputation recovery",
    ],
    pillars: [
      "Proactive monitoring that identifies reputation threats early",
      "Strategic response that protects and enhances brand perception",
      "Long-term strategy that builds sustainable positive reputation",
    ],
  };

  const journeySteps = [
    {
      title: "Reputation Audit",
      description: "We comprehensively assess your current online reputation across search results, reviews, social media, and all digital channels.",
      details: ["Search audit", "Review analysis", "Social listening", "Competitor benchmarking", "Sentiment scoring", "Threat identification"],
    },
    {
      title: "Strategy Development",
      description: "Our team creates a customized ORM strategy to address issues, suppress negative content, and build positive brand presence.",
      details: ["Action plan", "Content strategy", "Response protocols", "Crisis playbook", "Growth tactics", "Timeline & milestones"],
    },
    {
      title: "Implementation & Monitoring",
      description: "We execute the strategy with review management, content publishing, and 24/7 monitoring to protect your reputation.",
      details: ["Review responses", "Content publishing", "Social monitoring", "Negative suppression", "Brand mentions tracking", "Alert system"],
    },
    {
      title: "Ongoing Optimization",
      description: "Continuous monitoring, monthly reporting, and strategy refinement ensure sustained reputation improvement and protection.",
      details: ["Performance tracking", "Monthly reports", "Strategy adjustment", "Trend analysis", "Crisis prevention", "Long-term growth"],
    },
  ];

  const capabilities = [
    {
      title: "Review Management",
      content:
        "Comprehensive management of reviews across Google, Yelp, Trustpilot, Facebook, and industry-specific platforms with strategic responses.",
    },
    {
      title: "Negative Content Suppression",
      content:
        "Strategic content creation and SEO tactics to push down negative search results and improve first-page perception.",
    },
    {
      title: "Crisis Management",
      content:
        "Rapid response protocols and crisis communication strategies to minimize damage during reputation emergencies.",
    },
    {
      title: "Brand Monitoring",
      content:
        "24/7 monitoring of brand mentions, reviews, social media, news, and forums with real-time alerts for brand protection.",
    },
  ];

  const resultsData = {
    metrics: [
      { value: "4.3→4.8", label: "Average rating improvement" },
      { value: "78%", label: "Negative content suppression" },
      { value: "95%", label: "Positive sentiment increase" },
    ],
    description: "We deliver measurable reputation improvements that enhance trust, drive customer acquisition, and protect long-term brand equity.",
  };

  const outcomesData = {
    description: "We create comprehensive reputation management systems that protect your brand, build trust, and ensure positive online perception across all channels.",
    outcomes: [
      "24/7 brand and review monitoring across all platforms",
      "Professional responses to all reviews (positive & negative)",
      "Strategic negative content suppression campaigns",
      "Crisis management protocols and rapid response",
      "Monthly reputation reports and sentiment analysis",
      "Ongoing reputation building and protection",
    ],
  };

  const whyChooseUs = [
    {
      title: "Proven Track Record",
      content:
        "We've successfully managed reputation for 500+ brands, recovering from crises and building sustainable positive reputations.",
    },
    {
      title: "24/7 Protection",
      content:
        "Round-the-clock monitoring with instant alerts ensures we catch and address reputation threats before they spread.",
    },
    {
      title: "White-Hat Tactics",
      content:
        "We use only ethical, sustainable strategies that comply with platform guidelines and build long-term reputation equity.",
    },
    {
      title: "Crisis Experts",
      content:
        "Our experienced team has handled major reputation crises with proven protocols that minimize damage and accelerate recovery.",
    },
  ];

  const faqs = [
    {
      q: "How can you remove negative content about my business?",
      a: "We can't guarantee removal of legitimate content, but we use ethical strategies to suppress negative search results through positive content creation, SEO optimization, and strategic link building. We push negative content to page 2+ of search results while building positive first-page presence. For defamatory or false content, we work with legal teams on removal requests."
    },
    {
      q: "How long does it take to improve online reputation?",
      a: "Initial improvements can be seen in 30-60 days, with significant results in 3-6 months. Timeline depends on the severity of reputation issues, volume of negative content, and your industry. We provide monthly progress reports showing search result changes, review ratings, sentiment analysis, and brand mention trends."
    },
    {
      q: "How do you respond to negative reviews?",
      a: "We respond professionally and empathetically to all reviews, acknowledging concerns and offering solutions. Our goal is to show prospective customers that you care about feedback and resolve issues. We follow proven response frameworks that often turn dissatisfied customers into brand advocates while demonstrating excellent customer service to potential customers."
    },
    {
      q: "Can you help during a reputation crisis?",
      a: "Yes! We offer rapid-response crisis management services. Our team has handled major reputation emergencies including viral negative content, PR disasters, and coordinated attacks. We immediately implement crisis protocols: assess situation, develop response strategy, execute damage control, monitor spread, and coordinate with your team for consistent messaging."
    }
  ];

  return (
    <Fragment>
      <div className="br-app">
        <div id="page" className="page">
          <Header />
          <PageTitle
            pageTitle={"Reputation Management"}
            pagesub={"Branding & Design"}
          />
          <main className="page_content service-detials">
            <section className="pt-130 pb-130 gray-bg">
              <div className="container">
                <ServiceHero {...heroData} />
                <ServiceJourney title="Our Reputation Management Journey" steps={journeySteps} />
                <ServiceCapabilities capabilities={capabilities} />
                <ServiceResults {...resultsData} />
                <ServiceOutcomes {...outcomesData} />
                <ServiceWhyChooseUs items={whyChooseUs} />
                <FaqSection faqs={faqs} />
              </div>
            </section>
          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default ReputationManagement;
